interface TaskAddress {
  address: string;
  city: string;
  x: number;
  y: number;
  zipCode: string;
}

interface TaskExtendedData {
  distance: number;
  fromAddress: TaskAddress;
  toAddress: TaskAddress;
  movingStairType: 'NONE' | 'ELEVATOR' | 'STAIRS';
  movingType: 'ITEMS' | 'APARTMENT' | 'HOUSE';
  type: string;
}

interface TaskUser {
  id: string;
  firstName: string;
  name?: string;
  image?: string;
  rating?: number;
  profileImg?: string;
  completedTasks?: number;
  categories?: Array<{ title: string }>;
}

export interface TaskOffer {
  priceBid: number;
  createdAt: string;
  user?: TaskUser;
}

interface Task {
  id: string;
  title: string;
  description: string;
  offers: TaskOffer[];
  images: string[];
  createdAt: string;
  status: string;
  location: string;
  budget: number;
  user: TaskUser;
  extendedData?: TaskExtendedData;
}

export interface PriceGuideContent {
  FAQ_area: Array<any>;
  rich_text_title: string;
  rich_text: string;
  sub_header: string;
  market_insights: string;
  market_insights_title: string;
  youtube_title?: string;
  youtube_schema?: string;
  youtube_embed_id?: string;
}

export interface MostRelevantPriceGuide {
  id: number;
  similarity: number;
  slug: string;
  title: string;
  url: string;
}

export interface PriceGuideRange {
  min: number;
  middle: number;
  max: number;
}

export interface RelatedCategory {
  asCat: string;
  title: string;
  slug: string;
}

export interface RelatedVertical {
  uniqueId: number;
  title: string;
  asCat: number;
  slug: string;
  url: string;
}

export interface PriceData {
  min: number;
  middle: number;
  max: number;
}

export interface AIPriceGuideListItem {
  _id: string;
  slug: string;
  title: string;
  asCat: number;
  heroImg?: string;
  answer: string;
  price_guide: PriceData;
}

interface PaginatedResult {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AIPriceGuidesByCatResponse extends PaginatedResult {
  data: AIPriceGuideListItem[];
}

export interface TaskStatistics {
  totalSimilarTasks: number;
  averageOfferCount: number;
  averageOfferPrice: number;
  averageTimeToFirstOffer: number;
  offersWithinFiveHours: number;
}

export class AIPriceGuide {
  _id: string;
  formattedOutline: string;
  task_id: string;
  slug: string;
  answer: string;
  content: PriceGuideContent;
  createdAt: string;
  imgUrl: string;
  mostRelevantPriceGuide: MostRelevantPriceGuide | null;
  price_guide: PriceGuideRange;
  question: string;
  relatedCategory: RelatedCategory;
  relatedVertical: RelatedVertical;
  similarTasks: Array<any>;
  taskStatistics: TaskStatistics;
  title: string;
  title_tag: string;
  meta_description: string;
  whoICanHireBody: string;
  whoICanHireH2Title: string;
  task?: Task;
  mainCategory?: { slug: string };
  subCategory?: { slug: string };
  viewCount: number;
  imgAltAttribute: string;
  heroImg: string;
  // Set by the API when the requested URL's categories don't match the
  // guide's actual categories — the caller must redirect to this path
  canonicalPath?: string;
  // Worth-gate flag: the page still renders 200 but emits
  // `robots: noindex, follow` and is excluded from sitemaps and internal
  // link lists. Must be mapped in the constructor below — this class copies
  // fields explicitly, so an unmapped field is silently dropped.
  noindex?: boolean;

  constructor(data: Partial<AIPriceGuide>) {
    this._id = data._id || '';
    this.formattedOutline = data.formattedOutline || '';
    this.task_id = data.task_id || '';
    this.slug = data.slug || '';
    this.answer = data.answer || '';
    this.content = data.content || ({} as PriceGuideContent);
    this.createdAt = data.createdAt || '';
    this.imgUrl = data.imgUrl || '';
    this.mostRelevantPriceGuide =
      data.mostRelevantPriceGuide || ({} as MostRelevantPriceGuide);
    this.price_guide = data.price_guide || ({} as PriceGuideRange);
    this.question = data.question || '';
    this.relatedCategory = data.relatedCategory || ({} as RelatedCategory);
    this.relatedVertical = data.relatedVertical || ({} as RelatedVertical);
    this.similarTasks = data.similarTasks || [];
    this.taskStatistics = data.taskStatistics || ({} as TaskStatistics);
    this.title = data.title || '';
    this.title_tag = data.title_tag || '';
    this.meta_description = data.meta_description || '';
    this.whoICanHireBody = data.whoICanHireBody || '';
    this.whoICanHireH2Title = data.whoICanHireH2Title || '';
    this.task = data.task;
    this.mainCategory = data.mainCategory;
    this.subCategory = data.subCategory;
    this.viewCount = data.viewCount || 0;
    this.imgAltAttribute = data.imgAltAttribute || '';
    this.heroImg = data.heroImg || '';
    this.canonicalPath = data.canonicalPath;
    this.noindex = data.noindex;
  }
}
